import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { Cake } from 'lucide-react';
import { api } from '@/lib/api';
import { useMembers, useWorkspaceId } from '@/lib/hooks';
import { useAuth } from '@/lib/auth';
import { Avatar, Card } from '@/components/ui';
import { useStartDm } from '@/features/search/useStartDm';
import { PersonButton } from './PersonDrawer';

/** Avatar + name as one target that opens the person drawer (t132u); closing it returns here. */
export const PERSON = 'group flex min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-md -mx-1 px-1 py-0.5 text-left hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-slate-900 dark:hover:bg-slate-800 dark:focus-visible:outline-white';

/** Home widget: today's birthdays (with a quick "say happy birthday") and the upcoming list (backlog #4). */
export function BirthdaysWidget() {
  const ws = useWorkspaceId();
  const { userId } = useAuth();
  const { byId } = useMembers();
  const startDm = useStartDm();
  const q = useQuery({ queryKey: ['birthdays', ws], queryFn: () => api.birthdays(ws!, 30), enabled: Boolean(ws), staleTime: 10 * 60_000 });
  if (!q.data || (!q.data.today.length && !q.data.upcoming.length)) return null;
  return (
    <Card title={<span className="flex items-center gap-2"><Cake className="h-4 w-4 text-pink-500" />Birthdays</span>} actions={<Link to="/settings/account/birthday" className="text-xs font-medium link">Your birthday</Link>}>
      {q.data.today.length > 0 && (
        <ul className="mb-3 space-y-2 rounded-lg bg-pink-50 p-3 dark:bg-pink-950/30">
          {q.data.today.map(b => (
            <li key={b.user_id} className="flex items-center gap-2 text-sm">
              <PersonButton userId={b.user_id} name={b.display_name} className={PERSON} testId="birthday-person">
                <Avatar size={28} name={b.display_name} id={b.user_id} url={byId.get(b.user_id)?.avatar_url} />
                <span className="min-w-0 flex-1"><span className="font-semibold group-hover:underline">{b.display_name}</span> - today! 🎉</span>
              </PersonButton>
              {b.user_id !== userId && <button className="text-xs font-medium text-pink-700 hover:underline dark:text-pink-300" onClick={() => void startDm([b.user_id])}>Say happy birthday</button>}
            </li>
          ))}
        </ul>
      )}
      {q.data.upcoming.length > 0 && (
        <ul className="space-y-1.5">
          {q.data.upcoming.slice(0, 6).map(b => (
            <li key={b.user_id} className="flex items-center gap-2 text-sm">
              <PersonButton userId={b.user_id} name={b.display_name} className={PERSON} testId="birthday-person">
                <Avatar size={22} name={b.display_name} id={b.user_id} url={byId.get(b.user_id)?.avatar_url} />
                <span className="min-w-0 flex-1 truncate group-hover:underline">{b.display_name}</span>
              </PersonButton>
              <span className="text-xs text-slate-500">{DateTime.fromISO(b.date).toFormat('ccc d LLL')}{b.days_until === 1 ? ' · tomorrow' : ` · in ${b.days_until} days`}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
